/**
 * Matches Laravel ApiResponseTrait + bootstrap exception handlers:
 * { message: string|null, errors: array|Record<string, string[]> }
 */
export type ApiFieldErrors = Record<string, string[]>;

export type NormalizedApiErrors = {
  /** Field-keyed validation errors from the backend `errors` object. */
  fields: ApiFieldErrors;
  /** Non-field / flat errors (empty array items, 500 debug keys, etc.). */
  general: string[];
  /** All user-facing lines to display (fields flattened + general). */
  list: string[];
};

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

export function normalizeApiErrors(
  errors: unknown,
  httpStatus: number,
  fallbackMessage: string,
): NormalizedApiErrors {
  const fields: ApiFieldErrors = {};
  const general: string[] = [];

  if (errors === null || errors === undefined) {
    const list = fallbackMessage ? [fallbackMessage] : [];
    return { fields, general, list };
  }

  if (Array.isArray(errors)) {
    const flat = errors.map(String).filter(Boolean);
    general.push(...flat);
    const list = flat.length ? flat : fallbackMessage ? [fallbackMessage] : [];
    return { fields, general, list };
  }

  if (typeof errors === 'object') {
    for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
      if (isStringArray(value)) {
        const messages = value.filter(Boolean);
        if (messages.length) fields[key] = messages;
        continue;
      }

      if (httpStatus >= 500 && key === 'trace') continue;

      if (value === null || value === undefined || value === '') continue;

      const text = Array.isArray(value) ? value.map(String).join(', ') : String(value);
      general.push(`${key}: ${text}`);
    }
  } else if (errors !== '') {
    general.push(String(errors));
  }

  const fieldLines = Object.values(fields).flat();
  const list = [...fieldLines, ...general];
  if (!list.length && fallbackMessage) list.push(fallbackMessage);

  return { fields, general, list };
}
